/**
 * Safe formula parser for calculated columns in Report Builder.
 * Supports: +, -, *, / operators, column references, parentheses, numeric literals.
 * NO eval() — tokenizer + recursive descent parser.
 */

// ── Token Types ──────────────────────────────────────────────────────────────

const TokenType = {
  NUMBER: 'NUMBER',
  COLUMN: 'COLUMN',
  PLUS: 'PLUS',
  MINUS: 'MINUS',
  STAR: 'STAR',
  SLASH: 'SLASH',
  LPAREN: 'LPAREN',
  RPAREN: 'RPAREN',
  EOF: 'EOF',
};

// ── Tokenizer ────────────────────────────────────────────────────────────────

function tokenize(input) {
  const tokens = [];
  let pos = 0;

  while (pos < input.length) {
    const ch = input[pos];

    // Skip whitespace
    if (/\s/.test(ch)) {
      pos++;
      continue;
    }

    // Numeric literal (integer or decimal)
    if (/[0-9]/.test(ch) || (ch === '.' && pos + 1 < input.length && /[0-9]/.test(input[pos + 1]))) {
      let start = pos;
      let hasDot = false;
      if (ch === '.') {
        hasDot = true;
        pos++;
      }
      while (pos < input.length && (/[0-9]/.test(input[pos]) || (!hasDot && input[pos] === '.'))) {
        if (input[pos] === '.') hasDot = true;
        pos++;
      }
      tokens.push({ type: TokenType.NUMBER, value: input.slice(start, pos) });
      continue;
    }

    // Identifier (column reference): letters, digits, underscores — must start with letter or underscore
    if (/[a-zA-Z_]/.test(ch)) {
      let start = pos;
      while (pos < input.length && /[a-zA-Z0-9_]/.test(input[pos])) {
        pos++;
      }
      tokens.push({ type: TokenType.COLUMN, value: input.slice(start, pos) });
      continue;
    }

    // Single-character operators and parens
    switch (ch) {
      case '+': tokens.push({ type: TokenType.PLUS, value: '+' }); pos++; continue;
      case '-': tokens.push({ type: TokenType.MINUS, value: '-' }); pos++; continue;
      case '*': tokens.push({ type: TokenType.STAR, value: '*' }); pos++; continue;
      case '/': tokens.push({ type: TokenType.SLASH, value: '/' }); pos++; continue;
      case '(': tokens.push({ type: TokenType.LPAREN, value: '(' }); pos++; continue;
      case ')': tokens.push({ type: TokenType.RPAREN, value: ')' }); pos++; continue;
      default:
        throw new Error(`Unexpected character '${ch}' at position ${pos}`);
    }
  }

  tokens.push({ type: TokenType.EOF, value: null });
  return tokens;
}

// ── AST Node Constructors ────────────────────────────────────────────────────

function numericLiteral(value) {
  return { type: 'NumericLiteral', value: parseFloat(value) };
}

function columnReference(name) {
  return { type: 'ColumnReference', name };
}

function binaryExpression(operator, left, right) {
  return { type: 'BinaryExpression', operator, left, right };
}

function unaryExpression(operator, operand) {
  return { type: 'UnaryExpression', operator, operand };
}

// ── Recursive Descent Parser ─────────────────────────────────────────────────
//
// Grammar:
//   expression  →  term ( ('+' | '-') term )*
//   term        →  factor ( ('*' | '/') factor )*
//   factor      →  ('+' | '-') factor | primary
//   primary     →  NUMBER | COLUMN | '(' expression ')'

function createParser(tokens) {
  let pos = 0;

  function peek() {
    return tokens[pos];
  }

  function advance() {
    const token = tokens[pos];
    pos++;
    return token;
  }

  function expect(type) {
    const token = peek();
    if (token.type !== type) {
      throw new Error(`Expected ${type} but got ${token.type} ('${token.value}') at token position ${pos}`);
    }
    return advance();
  }

  // expression  →  term ( ('+' | '-') term )*
  function parseExpression() {
    let node = parseTerm();

    while (peek().type === TokenType.PLUS || peek().type === TokenType.MINUS) {
      const op = advance().value;
      const right = parseTerm();
      node = binaryExpression(op, node, right);
    }

    return node;
  }

  // term  →  factor ( ('*' | '/') factor )*
  function parseTerm() {
    let node = parseFactor();

    while (peek().type === TokenType.STAR || peek().type === TokenType.SLASH) {
      const op = advance().value;
      const right = parseFactor();
      node = binaryExpression(op, node, right);
    }

    return node;
  }

  // factor  →  ('+' | '-') factor | primary
  function parseFactor() {
    if (peek().type === TokenType.MINUS) {
      advance();
      const operand = parseFactor();
      return unaryExpression('-', operand);
    }
    if (peek().type === TokenType.PLUS) {
      advance();
      return parseFactor();
    }
    return parsePrimary();
  }

  // primary  →  NUMBER | COLUMN | '(' expression ')'
  function parsePrimary() {
    const token = peek();

    if (token.type === TokenType.NUMBER) {
      advance();
      return numericLiteral(token.value);
    }

    if (token.type === TokenType.COLUMN) {
      advance();
      return columnReference(token.value);
    }

    if (token.type === TokenType.LPAREN) {
      advance();
      const expr = parseExpression();
      expect(TokenType.RPAREN);
      return expr;
    }

    throw new Error(`Unexpected token ${token.type} ('${token.value}') at token position ${pos}`);
  }

  // Entry point
  function parse() {
    const ast = parseExpression();
    if (peek().type !== TokenType.EOF) {
      throw new Error(`Unexpected token ${peek().type} ('${peek().value}') after end of expression`);
    }
    return ast;
  }

  return { parse };
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Parse a formula string into an AST.
 * @param {string} formulaString - e.g. "total_pay - paid_amount"
 * @returns {object} AST
 */
export function parseFormula(formulaString) {
  if (!formulaString || typeof formulaString !== 'string') {
    throw new Error('Formula must be a non-empty string');
  }
  const trimmed = formulaString.trim();
  if (trimmed.length === 0) {
    throw new Error('Formula must be a non-empty string');
  }
  const tokens = tokenize(trimmed);
  const parser = createParser(tokens);
  return parser.parse();
}

/**
 * Evaluate an AST against a data row, returning a numeric result.
 * @param {object} ast - AST produced by parseFormula()
 * @param {object} rowData - Key/value pairs for the current row, e.g. { total_pay: 100, paid_amount: 40 }
 * @returns {number}
 */
export function evaluateFormula(ast, rowData) {
  if (!ast || !ast.type) {
    throw new Error('Invalid AST node');
  }

  switch (ast.type) {
    case 'NumericLiteral':
      return ast.value;

    case 'ColumnReference': {
      const val = rowData[ast.name];
      if (val === undefined || val === null) {
        return 0; // Treat missing/null values as 0
      }
      const num = Number(val);
      if (isNaN(num)) {
        throw new Error(`Column '${ast.name}' has non-numeric value: ${val}`);
      }
      return num;
    }

    case 'UnaryExpression':
      if (ast.operator === '-') {
        return -evaluateFormula(ast.operand, rowData);
      }
      throw new Error(`Unknown unary operator: ${ast.operator}`);

    case 'BinaryExpression': {
      const left = evaluateFormula(ast.left, rowData);
      const right = evaluateFormula(ast.right, rowData);
      switch (ast.operator) {
        case '+': return left + right;
        case '-': return left - right;
        case '*': return left * right;
        case '/':
          if (right === 0) return 0; // Avoid division by zero — return 0 instead of Infinity
          return left / right;
        default:
          throw new Error(`Unknown binary operator: ${ast.operator}`);
      }
    }

    default:
      throw new Error(`Unknown AST node type: ${ast.type}`);
  }
}

/**
 * Validate a formula string against a set of available column names.
 * @param {string} formulaString - The formula to validate
 * @param {string[]} availableColumns - List of valid column keys
 * @returns {{ valid: boolean, error?: string, referencedColumns: string[] }}
 */
export function validateFormula(formulaString, availableColumns) {
  const referencedColumns = [];

  try {
    const ast = parseFormula(formulaString);

    // Walk AST to collect column references
    function collectColumns(node) {
      if (!node) return;
      switch (node.type) {
        case 'ColumnReference':
          if (!referencedColumns.includes(node.name)) {
            referencedColumns.push(node.name);
          }
          break;
        case 'BinaryExpression':
          collectColumns(node.left);
          collectColumns(node.right);
          break;
        case 'UnaryExpression':
          collectColumns(node.operand);
          break;
        case 'NumericLiteral':
          break;
        default:
          break;
      }
    }

    collectColumns(ast);

    // Check that all referenced columns are in the available set
    const columnSet = new Set(availableColumns);
    const unknownColumns = referencedColumns.filter(col => !columnSet.has(col));

    if (unknownColumns.length > 0) {
      return {
        valid: false,
        error: `Unknown column(s): ${unknownColumns.join(', ')}`,
        referencedColumns,
      };
    }

    return { valid: true, referencedColumns };
  } catch (err) {
    return {
      valid: false,
      error: err.message,
      referencedColumns,
    };
  }
}
